import { Icon } from "@tutor/ui";
import { useId, useState, type ReactNode } from "react";

// Compact controls for the goals panel: one line each, so the whole panel fits a phone
// screen (the same approach as the display settings sheet, #116). Native elements
// underneath (checkbox, button, select), so keyboards and screen readers work as usual.

/** A row: a short label on the left, its control on the right, an optional ⓘ hint. */
export function Row(props: {
  label: string;
  /** Id of the label, for the control's aria-labelledby. */
  labelId: string;
  hint?: string;
  /** The ⓘ button's name, when there is a hint. */
  hintLabel?: string;
  children: ReactNode;
}) {
  const hintId = useId();
  const [open, setOpen] = useState(false);
  return (
    <div className="pm-row">
      <div className="pm-row__line">
        <span id={props.labelId} className="pm-row__label">
          {props.label}
        </span>
        {props.hint === undefined ? null : (
          <button
            type="button"
            className="pm-info"
            aria-label={props.hintLabel}
            aria-expanded={open}
            aria-controls={hintId}
            onClick={() => {
              setOpen(!open);
            }}
          >
            <Icon name="info" size={18} />
          </button>
        )}
        <span className="pm-row__control">{props.children}</span>
      </div>
      {props.hint === undefined ? null : (
        <p id={hintId} className="pm-row__hint" hidden={!open}>
          {props.hint}
        </p>
      )}
    </div>
  );
}

/** A number changed with − and + buttons, clamped to [min, max]. */
export function Stepper(props: {
  labelId: string;
  value: number;
  min: number;
  max: number;
  step: number;
  /** The value as shown and as read out, e.g. "45 min". */
  format: (value: number) => string;
  lessLabel: string;
  moreLabel: string;
  onChange: (value: number) => void;
}) {
  const { value, min, max, step } = props;
  const set = (next: number) => {
    props.onChange(Math.min(max, Math.max(min, next)));
  };
  return (
    <span className="pm-stepper" role="group" aria-labelledby={props.labelId}>
      <button
        type="button"
        className="pm-stepper__button"
        aria-label={props.lessLabel}
        disabled={value <= min}
        onClick={() => {
          // Back onto the step grid first, e.g. 47 → 45.
          set(value % step === 0 ? value - step : value - (value % step));
        }}
      >
        −
      </button>
      <output className="pm-stepper__value" aria-live="polite">
        {props.format(value)}
      </output>
      <button
        type="button"
        className="pm-stepper__button"
        aria-label={props.moreLabel}
        disabled={value >= max}
        onClick={() => {
          set(value - (value % step) + step);
        }}
      >
        +
      </button>
    </span>
  );
}

/** On/off chips for a set of options, wrapping onto two lines at most. */
export function Chips<T extends string>(props: {
  labelId: string;
  options: readonly { readonly value: T; readonly label: string }[];
  selected: readonly T[];
  onToggle: (value: T, on: boolean) => void;
}) {
  return (
    <div className="pm-chips" role="group" aria-labelledby={props.labelId}>
      {props.options.map((option) => {
        const on = props.selected.includes(option.value);
        return (
          <label key={option.value} className="pm-chip" data-on={on ? "true" : undefined}>
            <input
              type="checkbox"
              checked={on}
              onChange={(e) => {
                props.onToggle(option.value, e.target.checked);
              }}
            />
            {on ? <Icon name="check" size={16} className="pm-chip__icon" /> : null}
            <span>{option.label}</span>
          </label>
        );
      })}
    </div>
  );
}

/** An on/off switch with role="switch", labelled by its row. */
export function Toggle(props: {
  labelId: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      className="ui-switch__control"
      aria-checked={props.checked}
      aria-labelledby={props.labelId}
      onClick={() => {
        props.onChange(!props.checked);
      }}
    >
      <span className="ui-switch__thumb" />
    </button>
  );
}

/** Minutes since midnight as "21:00": always 24-hour, whatever the browser's locale. */
export function clock(minutes: number): string {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

const QUARTERS = Array.from({ length: 96 }, (_, i) => i * 15);

/** A time of day in quarter hours, shown in 24-hour format. */
export function TimeSelect(props: {
  label: string;
  value: number;
  onChange: (minutes: number) => void;
}) {
  // A saved time off the quarter-hour grid stays selectable.
  const options = QUARTERS.includes(props.value)
    ? QUARTERS
    : [...QUARTERS, props.value].sort((a, b) => a - b);
  return (
    <select
      className="pm-time"
      aria-label={props.label}
      value={props.value}
      onChange={(e) => {
        props.onChange(Number(e.target.value));
      }}
    >
      {options.map((minutes) => (
        <option key={minutes} value={minutes}>
          {clock(minutes)}
        </option>
      ))}
    </select>
  );
}
